import React from 'react'
import {
  Body,
  HelpCenterArticleList,
  Grid,
  Label,
  Link,
  PageSection as Section,
  PageHeader,
} from '@superhi/design'

import { TopicsList, TopicsWrapper } from './styles'

import { buildPath } from 'src/routing/utils'
import Main from 'src/components/Main'
import Page from 'src/components/Page'
import NoResults from 'src/components/NoResults'
import { API } from 'src/api'
import SEO from 'src/components/SEO'

const GRID_1_ROW_GAP = { small: 40 }
const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 2,
  large: 4,
}
const GRID_1_CELL_SPAN_2: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 6,
  large: 8,
}

type Props = API.HelpCenterTopic & {
  topicSlug?: string | 'none'
  seoTitle?: string
}

const HelpCenterLayout: React.FC<React.PropsWithChildren<Props>> = ({
  topicSlug,
  helpCenterArticles,
  helpCenterTopics,
  seoTitle,
}) => {
  const articles = helpCenterArticles?.nodes || []

  const topics = React.useMemo(
    () => [
      {
        slug: undefined,
        href: buildPath('help-center-index'),
        display: 'All topics',
      },
      ...(helpCenterTopics || []).map((topic) => ({
        slug: topic.slug,
        href: buildPath('help-center-topics-view', {
          params: { slug: topic.slug },
        }),
        display: topic.title,
      })),
    ],
    [helpCenterTopics],
  )

  return (
    <Page>
      <SEO title={`${seoTitle ? `${seoTitle} – ` : ''}Frequently Asked Questions`} />

      <Main>
        <PageHeader
          title="Help Center"
          subtitle="Things we get asked a lot (but that we’re happy to answer!)"
          backgroundVersion="YELLOW_02"
        />

        <Section>
          <Grid rowGap={GRID_1_ROW_GAP}>
            <Grid.Cell span={GRID_1_CELL_SPAN_1}>
              <TopicsWrapper as={'nav'} aria-labelledby="topics-nav-label">
                <Label id="topics-nav-label" color="GREY_70" as={'h2'}>
                  Topics
                </Label>

                <TopicsList>
                  {topics.map((topic, i) => (
                    <div key={topic.slug || 0 + i}>
                      <Body level="2" bold={topic.slug === topicSlug}>
                        <Link
                          href={topic.href}
                          color={topic.slug === topicSlug ? 'BLUE_50' : 'GREY_90'}
                          hoverColor="BLUE_50"
                          aria-current={topic.slug === topicSlug ? 'true' : 'false'}
                        >
                          {topic.display}
                        </Link>
                      </Body>
                    </div>
                  ))}
                </TopicsList>
              </TopicsWrapper>
            </Grid.Cell>

            <Grid.Cell span={GRID_1_CELL_SPAN_2}>
              {articles.length > 0 ? (
                <HelpCenterArticleList articles={articles} />
              ) : (
                <NoResults noPadding />
              )}
            </Grid.Cell>
          </Grid>
        </Section>
      </Main>
    </Page>
  )
}

export default HelpCenterLayout
